import type {
  GetParticipationsByEventAndAttendeesCommandOutput,
  MultiShiftParticipation,
  Participation,
  Person,
} from '@amzn/red-velvet-api';

interface CorrectionItem {
  id: number;
  correctedValue: string;
  originalValue: string;
  error: string;
  input: string;
  isEditing: boolean;
  isResolved: boolean;
  isValidating: boolean;
}

/**
 * Deduplicates participations using composite key of volunteerAlias + shiftId.
 * This ensures we keep multiple participations by the same person in different shifts.
 *
 * @param participations - Array of participations to deduplicate
 * @returns Deduplicated array of participations
 */
function deduplicateParticipations(participations: Participation[]): Participation[] {
  const seen = new Set<string>();
  return participations.filter((p) => {
    if (!p.volunteerAlias || !p.shiftId) {
      // remove if no valid alias or shiftId
      return false;
    }
    const key = `${p.volunteerAlias || ''}-${p.shiftId || ''}`;
    if (!key || seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

/**
 * Deduplicates multi-shift participations by alias, merging participations for same user.
 *
 * @param multiShift - Array of multi-shift participations to deduplicate
 * @returns Deduplicated array with merged participations per user
 */
function deduplicateMultiShift(multiShift: MultiShiftParticipation[]): MultiShiftParticipation[] {
  const byAlias = new Map<string, MultiShiftParticipation>();
  multiShift.forEach((ms) => {
    const existing = byAlias.get(ms.alias);
    if (existing) {
      // Merge participations for the same alias
      const mergedParticipations = deduplicateParticipations([
        ...existing.participations,
        ...ms.participations,
      ]);
      byAlias.set(ms.alias, { alias: ms.alias, participations: mergedParticipations });
    } else {
      byAlias.set(ms.alias, ms);
    }
  });
  return Array.from(byAlias.values());
}

/**
 * Deduplicates people using alias as the unique key.
 *
 * @param people - Array of people to deduplicate
 * @returns Deduplicated array of people
 */
function deduplicatePeople(people: Person[]): Person[] {
  const seen = new Set<string>();
  return people.filter((p) => {
    const key = p.alias;
    if (!key || seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

/**
 * Merges validation data from the initial API response with resolved responses
 * from user corrected identifiers and selections. Handles deduplication of participations with signups,
 * multi-shift, people with already-tracked attendance and people without participation (no signup nor attendance).
 *
 * @param validationData - Initial validation response from the API
 * @param resolvedResponses - Array of validation responses from corrected identifiers
 * @param items - Array of correction items tracking resolution status
 * @returns Merged validation data with deduplicated entries
 */
export function mergeValidationResponses(
  validationData: GetParticipationsByEventAndAttendeesCommandOutput,
  resolvedResponses: GetParticipationsByEventAndAttendeesCommandOutput[],
  items: CorrectionItem[]
): GetParticipationsByEventAndAttendeesCommandOutput {
  // If no changes, early return
  if (resolvedResponses.length === 0) {
    return validationData;
  }

  let allParticipations = [...validationData.participations];
  let allTrackedParticipations = [...validationData.trackedParticipations];
  let allMultiShift = [...(validationData.multiShiftParticipations || [])];
  let allPeopleWithoutParticipation = [...validationData.peopleWithoutParticipation];
  let totalDuplicates = validationData.duplicateCount;

  // Set originalPeopleAliases from original data
  const originalPeopleAliases = new Set<string>();
  allParticipations.forEach((p) => {
    if (p.volunteerAlias) originalPeopleAliases.add(p.volunteerAlias);
  });
  allTrackedParticipations.forEach((p) => {
    if (p.volunteerAlias) originalPeopleAliases.add(p.volunteerAlias);
  });
  allMultiShift.forEach((ms) => {
    if (ms.alias) originalPeopleAliases.add(ms.alias);
  });
  allPeopleWithoutParticipation.forEach((p) => {
    if (p.alias) originalPeopleAliases.add(p.alias);
  });

  // Track how many corrected identifiers resolve to people already seen
  // (either in original data OR in previously processed resolved responses)
  let duplicateCorrectionsCount = 0;
  const seenAliases = new Set<string>(originalPeopleAliases);

  resolvedResponses.forEach((response) => {
    // Check if this resolved response contains people already seen
    let hasMatchInSeen = false;
    const aliasesInThisResponse = new Set<string>();

    response.participations.forEach((p) => {
      if (seenAliases.has(p.volunteerAlias)) {
        hasMatchInSeen = true;
      }
      aliasesInThisResponse.add(p.volunteerAlias);
    });
    response.trackedParticipations.forEach((p) => {
      if (seenAliases.has(p.volunteerAlias)) {
        hasMatchInSeen = true;
      }
      aliasesInThisResponse.add(p.volunteerAlias);
    });
    response.multiShiftParticipations?.forEach((ms) => {
      if (seenAliases.has(ms.alias)) {
        hasMatchInSeen = true;
      }
      aliasesInThisResponse.add(ms.alias);
    });
    response.peopleWithoutParticipation.forEach((p) => {
      if (p.alias) {
        if (seenAliases.has(p.alias)) {
          hasMatchInSeen = true;
        }
        aliasesInThisResponse.add(p.alias);
      }
    });

    // If this corrected identifier resolved to someone already seen, count it as duplicate
    if (hasMatchInSeen) {
      duplicateCorrectionsCount++;
    }

    // Add all aliases from this response to seenAliases for next iteration
    aliasesInThisResponse.forEach((alias) => seenAliases.add(alias));

    // Merge the data to keep track of the corrected data as well
    allParticipations = [...allParticipations, ...response.participations];
    allTrackedParticipations = [...allTrackedParticipations, ...response.trackedParticipations];
    allMultiShift = [...allMultiShift, ...(response.multiShiftParticipations || [])];
    allPeopleWithoutParticipation = [
      ...allPeopleWithoutParticipation,
      ...response.peopleWithoutParticipation,
    ];
  });

  // Include duplicates from corrected identifiers that match already seen data
  totalDuplicates += duplicateCorrectionsCount;

  // Deduplicate data for clean output (duplicate counting already done above)
  const deduplicatedParticipations = deduplicateParticipations(allParticipations);
  const deduplicatedTrackedParticipations = deduplicateParticipations(allTrackedParticipations);
  const deduplicatedMultiShift = deduplicateMultiShift(allMultiShift);
  const deduplicatedPeopleWithoutParticipation = deduplicatePeople(allPeopleWithoutParticipation);

  return {
    ...validationData,
    participations: deduplicatedParticipations,
    trackedParticipations: deduplicatedTrackedParticipations,
    multiShiftParticipations: deduplicatedMultiShift,
    peopleWithoutParticipation: deduplicatedPeopleWithoutParticipation,
    duplicateCount: totalDuplicates, // this is no longer in use but I leave it here as it doesn't hurt and the effort to calculate was done already.
    // Make sure there are still no items with unresolved status
    failedIdentifiers: items.filter((item) => !item.isResolved).map((item) => item.input),
  };
}
